import {FormEvent, useEffect, useState} from "react";
import {Building2, X} from "lucide-react";
import {Button} from "./ui/Button";
import {Input, Label, Textarea} from "./ui/Input";
import {useToast} from "./ui/Toast";
import {updateOrganization} from "../services/apiService";
import {OrganizationFA} from "../interfaces/global-interfaces";
import {getErrorMessage} from "../utils/errors";

interface EditOrganizationModalProps {
    isOpen: boolean;
    organization: OrganizationFA;
    onClose: () => void;
    onSaved: (organization: OrganizationFA) => void;
}

export const EditOrganizationModal = ({isOpen, organization, onClose, onSaved}: EditOrganizationModalProps) => {
    const [name, setName] = useState(organization.name);
    const [description, setDescription] = useState(organization.description ?? "");
    const [submitting, setSubmitting] = useState(false);
    const {showToast} = useToast();

    useEffect(() => {
        if (isOpen) {
            setName(organization.name);
            setDescription(organization.description ?? "");
        }
    }, [isOpen, organization]);

    if (!isOpen) return null;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!name.trim() || submitting) return;

        setSubmitting(true);
        try {
            const {organization: updated} = await updateOrganization(organization._id, {
                name: name.trim(),
                description: description.trim(),
            });
            showToast("Organization updated.", "success");
            onSaved(updated);
            onClose();
        } catch (err) {
            console.error("Error updating organization:", err);
            showToast(getErrorMessage(err, "Couldn't update the organization. Please try again."), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4 animate-fade-in animate-duration-150">
            <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-lg animate-zoom-in animate-duration-200">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <div className="size-8 rounded-lg bg-primary-tint text-primary flex items-center justify-center">
                            <Building2 size={16}/>
                        </div>
                        <h2 className="text-base font-display font-bold text-ink">Edit Organization</h2>
                    </div>
                    <button
                        type="button"
                        onClick={() => !submitting && onClose()}
                        aria-label="Close"
                        className="text-ink-faint hover:text-ink transition-colors"
                    >
                        <X size={16}/>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label>Name</Label>
                        <Input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Acme Inc."
                            autoFocus
                            required
                        />
                    </div>

                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What's this organization for?"
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" loading={submitting} disabled={!name.trim()}>
                            Save
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
